import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { PRIVACY_VERSION, formatLegalVersion } from '../config/legalVersions';
import { openCookiePreferences } from '../services/cookiePreferences';

const PrivacyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '0',
    },
    header: {
      backgroundColor: '#22B8E0',
      padding: '24px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    },
    headerContent: {
      maxWidth: '800px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    backButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      color: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    headerTitle: {
      color: 'white',
      fontSize: '24px',
      fontWeight: '700',
      margin: 0,
      flex: 1,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '32px 20px',
    },
    lastUpdated: {
      color: '#64748b',
      fontSize: '14px',
      marginBottom: '24px',
    },
    section: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    sectionTitle: {
      color: '#1e293b',
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '2px solid #22B8E0',
    },
    paragraph: {
      color: '#475569',
      fontSize: '15px',
      lineHeight: '1.7',
      marginBottom: '16px',
    },
    list: {
      color: '#475569',
      fontSize: '15px',
      lineHeight: '1.7',
      paddingLeft: '20px',
      marginBottom: '16px',
    },
    listItem: {
      marginBottom: '8px',
    },
    strong: {
      color: '#1e293b',
      fontWeight: '600',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '16px',
      fontSize: '14px',
    },
    tableHeader: {
      backgroundColor: '#f1f5f9',
      padding: '12px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#1e293b',
      borderBottom: '2px solid #e2e8f0',
    },
    tableCell: {
      padding: '12px',
      borderBottom: '1px solid #e2e8f0',
      color: '#475569',
    },
    contactBox: {
      backgroundColor: '#f0f9ff',
      borderRadius: '8px',
      padding: '16px',
      borderLeft: '4px solid #22B8E0',
    },
    link: {
      color: '#22B8E0',
      textDecoration: 'none',
    },
    footer: {
      textAlign: 'center',
      padding: '24px 20px',
      color: '#94a3b8',
      fontSize: '14px',
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            <span>&larr;</span> {t('common.back')}
          </button>
          <h1 style={styles.headerTitle}>{t('legal.privacy_title')}</h1>
        </div>
      </header>

      <main style={styles.content}>
        <p style={styles.lastUpdated}>
          Version {PRIVACY_VERSION} · Last updated: {formatLegalVersion(PRIVACY_VERSION)}
        </p>

        {/* Introduction */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction & Data Controller</h2>
          <p style={styles.paragraph}>
            Welcome to <strong style={styles.strong}>ScrollEd</strong> ("we," "our," or "us"), available at{' '}
            <a href="https://scrolled.eu" target="_blank" rel="noopener noreferrer" style={styles.link}>scrolled.eu</a>.
            This Policy explains how personal data is collected, used, shared, retained, and protected, and the rights
            available under the EU General Data Protection Regulation (GDPR - Regulation (EU) 2016/679). It also
            describes transparency measures relevant to the EU Artificial Intelligence Act (Regulation (EU) 2024/1689).
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Data Controller:</strong> The Service is operated by an independent individual
            operator ("Data Controller"). For any privacy
            inquiries, data protection requests, or controller information, you can contact us directly at{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Data Protection Officer:</strong> ScrollEd has not designated a Data
            Protection Officer (DPO). Whether designation is mandatory under GDPR Article 37 depends on the nature,
            scope, and scale of the processing. The Controller will keep that assessment under review. Until a DPO is
            designated, all privacy and data-protection enquiries should be sent directly to the Controller at{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
          </p>
        </section>

        {/* Information We Collect */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Collect</h2>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>2.1 Information You Provide</strong>
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Data Type</th>
                <th style={styles.tableHeader}>Purpose</th>
                <th style={styles.tableHeader}>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>Email address</td>
                <td style={styles.tableCell}>Account creation, authentication, password recovery</td>
                <td style={styles.tableCell}>Yes</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Password</td>
                <td style={styles.tableCell}>Managed by Supabase Auth using one-way password hashing; ScrollEd does not store a readable password</td>
                <td style={styles.tableCell}>For password sign-in only</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Display name</td>
                <td style={styles.tableCell}>Profile personalization</td>
                <td style={styles.tableCell}>No</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Profile photo</td>
                <td style={styles.tableCell}>Profile personalization</td>
                <td style={styles.tableCell}>No</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Google OAuth identifiers and profile fields</td>
                <td style={styles.tableCell}>Optional account creation and sign-in through Google</td>
                <td style={styles.tableCell}>Only if you choose Google sign-in</td>
              </tr>
            </tbody>
          </table>
          <p style={styles.paragraph}>
            Account credentials or a supported OAuth sign-in are required to provide authenticated account features;
            without them, ScrollEd cannot create or authenticate an account. Uploading a document is optional, but the
            requested AI-generation feature cannot operate without receiving the selected document. Analytics consent
            is optional and refusal does not prevent access to the core Service. Data is obtained directly from you,
            automatically from your use of the Service, or from Google when you deliberately choose Google sign-in.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>2.2 Documents You Upload</strong>
          </p>
          <p style={styles.paragraph}>
            When you upload PDF documents to ScrollEd for processing:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong style={styles.strong}>PDF Files:</strong> Stored to provide the Service until deleted through an available document or account process, subject to lawful retention and provider backup cycles</li>
            <li style={styles.listItem}><strong style={styles.strong}>Extracted Text:</strong> Used to generate educational content</li>
            <li style={styles.listItem}><strong style={styles.strong}>Generated Content:</strong> Learning cards, quizzes, and summaries created from your documents</li>
          </ul>
          <p style={styles.paragraph}>
            Uploaded documents may contain personal data about you or other people. You must have lawful authority to
            provide that data. The Service is not designed or approved for special-category data (such as health,
            biometric, genetic, political, religious, trade-union, or sexual-life data), criminal-record data, or
            sensitive records concerning children. Do not upload such information. If we identify prohibited sensitive
            data, we may restrict processing, ask you to remove it, or delete it where legally permitted.
          </p>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>2.3 Automatically Collected Information</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong style={styles.strong}>Device Information:</strong> Device type, operating system (for app compatibility)</li>
            <li style={styles.listItem}><strong style={styles.strong}>Usage Data:</strong> Features used, learning progress, quiz responses</li>
            <li style={styles.listItem}><strong style={styles.strong}>Request & Error Logs:</strong> Timestamps, IP address, user agent, route/status metadata, and diagnostic information generated by hosting, authentication, and application systems</li>
            <li style={styles.listItem}><strong style={styles.strong}>Consent-based Web Analytics:</strong> Page and device information is sent to Google Analytics only after you enable analytics in Cookie settings</li>
          </ul>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>2.4 Information We Do Not Intentionally Request</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Location data</li>
            <li style={styles.listItem}>Contact lists</li>
            <li style={styles.listItem}>SMS or call logs</li>
            <li style={styles.listItem}>Financial or payment information</li>
            <li style={styles.listItem}>Microphone or camera data</li>
          </ul>
        </section>

        {/* How We Use Your Information & Legal Bases */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Purposes of Processing & Legal Bases (GDPR Art. 6)</h2>
          <p style={styles.paragraph}>
            Under GDPR Article 6, we only process your personal data where a valid lawful basis applies:
          </p>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Processing Activity</th>
                <th style={styles.tableHeader}>Purpose</th>
                <th style={styles.tableHeader}>Legal Basis (GDPR Art. 6)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>Account Management</td>
                <td style={styles.tableCell}>User registration, authentication, profile storage</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(b)</strong> — Performance of Contract</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>AI Educational Generation</td>
                <td style={styles.tableCell}>Processing uploaded PDFs to extract chapters, summaries, and quizzes</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(b)</strong> — Performance of Contract (User-initiated service)</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Learning Progress & Analytics</td>
                <td style={styles.tableCell}>Tracking study streaks, quiz accuracy, card completion</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(f)</strong> — Legitimate Interest (Platform efficacy & UX improvement)</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Service Security & Bug Reporting</td>
                <td style={styles.tableCell}>Preventing abuse, diagnosing errors, handling crash reports</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(f)</strong> — Legitimate Interest (System integrity & security)</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Legal Compliance & Claims</td>
                <td style={styles.tableCell}>Responding to binding legal requests, enforcing rights, and establishing, exercising, or defending legal claims</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(c)</strong> — Legal Obligation, or <strong style={styles.strong}>Art. 6(1)(f)</strong> — Legitimate Interests, as applicable</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Transactional Emails</td>
                <td style={styles.tableCell}>Account verification, security notices, password reset</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(b)</strong> — Performance of Contract</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Non-Essential Cookies</td>
                <td style={styles.tableCell}>Optional Google Analytics cookies and similar storage</td>
                <td style={styles.tableCell}><strong style={styles.strong}>Art. 6(1)(a)</strong> — Freely Given Consent</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Third-Party Data Processors */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Data Processors & Third-Party Services</h2>
          <p style={styles.paragraph}>
            We use the following processors and third-party services under their applicable service terms and, where
            required, data-processing terms. A provider may also process limited account or usage information as an
            independent controller under its own privacy notice.
          </p>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Processor</th>
                <th style={styles.tableHeader}>Location</th>
                <th style={styles.tableHeader}>Purpose</th>
                <th style={styles.tableHeader}>Transfer Safeguards</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>Supabase Inc.</strong></td>
                <td style={styles.tableCell}>EU project region; provider support and subprocessors may operate globally</td>
                <td style={styles.tableCell}>Authentication, PostgreSQL database, file storage</td>
                <td style={styles.tableCell}>Provider DPA and SCCs where an international transfer requires them</td>
              </tr>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>OpenAI LLC</strong></td>
                <td style={styles.tableCell}>United States</td>
                <td style={styles.tableCell}>AI semantic extraction, chapter generation, and quizzes</td>
                <td style={styles.tableCell}>Provider data-processing terms and SCCs where applicable</td>
              </tr>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>Resend Inc.</strong></td>
                <td style={styles.tableCell}>United States</td>
                <td style={styles.tableCell}>Transactional email delivery and bug report submission</td>
                <td style={styles.tableCell}>Standard Contractual Clauses (SCCs)</td>
              </tr>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>Railway Corp.</strong></td>
                <td style={styles.tableCell}>Global cloud infrastructure</td>
                <td style={styles.tableCell}>Application hosting, networking, and operational logs</td>
                <td style={styles.tableCell}>Provider contractual terms; DPA/SCC coverage must apply where required</td>
              </tr>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>Google</strong></td>
                <td style={styles.tableCell}>Global; Google Ireland serves EEA users</td>
                <td style={styles.tableCell}>Optional Google account sign-in</td>
                <td style={styles.tableCell}>Google processes OAuth data under its applicable terms and privacy notice</td>
              </tr>
              <tr>
                <td style={styles.tableCell}><strong style={styles.strong}>Google Analytics</strong></td>
                <td style={styles.tableCell}>Global; Google Ireland serves EEA users</td>
                <td style={styles.tableCell}>Optional web-usage measurement, loaded only after analytics consent</td>
                <td style={styles.tableCell}>Consent controls plus Google's applicable data terms and transfer mechanisms</td>
              </tr>
            </tbody>
          </table>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>OpenAI processing:</strong> OpenAI states that API inputs and outputs are not
            used to train or improve its models by default unless the account holder expressly opts in. Under the
            default API controls, abuse-monitoring logs containing customer content may be retained for up to 30 days,
            unless a different approved retention control applies. ScrollEd does not promise Zero Data Retention unless
            that setting has been separately verified for the production account.
          </p>
          <p style={styles.paragraph}>
            For more information, review:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Supabase Privacy Policy
              </a>
            </li>
            <li style={styles.listItem}>
              <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                OpenAI Privacy Policy & Enterprise Data Commitments
              </a>
            </li>
            <li style={styles.listItem}>
              <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Resend Privacy Policy
              </a>
            </li>
            <li style={styles.listItem}>
              <a href="https://railway.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Railway Privacy Policy
              </a>
            </li>
            <li style={styles.listItem}>
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                Google Privacy Policy
              </a>
            </li>
          </ul>
        </section>

        {/* Cookie and analytics controls */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Cookies & Analytics Controls</h2>
          <p style={styles.paragraph}>
            ScrollEd uses local storage and similar technology needed for sign-in, security, language, and interface
            preferences. Google Analytics is optional: its script is not loaded until you enable the Analytics category
            in Cookie settings. Rejecting analytics does not prevent use of the core Service.
          </p>
          <p style={styles.paragraph}>
            You can reopen Cookie settings at any time to change or withdraw your choice. Withdrawal stops future
            analytics collection on that browser and removes recognised ScrollEd analytics identifiers where the
            browser permits. It does not affect processing that was lawful before withdrawal.
          </p>
          <button
            type="button"
            onClick={openCookiePreferences}
            className="inline-flex min-h-11 items-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            {t('settings.cookie_settings', { defaultValue: 'Cookie settings' })}
          </button>
        </section>

        {/* Data Storage & Security */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Data Storage & Security</h2>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>6.1 Where We Store Your Data</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Account data is stored in the Supabase cloud database</li>
            <li style={styles.listItem}>Uploaded documents are stored using Supabase Storage</li>
            <li style={styles.listItem}>Generated content is stored in the application database</li>
          </ul>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>6.2 Security Measures</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>HTTPS/TLS for supported connections between users, ScrollEd, and service providers</li>
            <li style={styles.listItem}>Provider storage protections, access controls, and encryption where supported</li>
            <li style={styles.listItem}>One-way password hashing managed by Supabase Auth</li>
            <li style={styles.listItem}>Row Level Security (RLS) and application authorization controls designed to restrict account data by ownership and service role</li>
            <li style={styles.listItem}>Security monitoring, patching, and tracked remediation of identified risks</li>
          </ul>

          <p style={styles.paragraph}>
            <strong style={styles.strong}>6.3 Data Retention Schedules (GDPR Art. 5(1)(e))</strong>
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Data Category</th>
                <th style={styles.tableHeader}>Retention Period</th>
                <th style={styles.tableHeader}>Deletion Rule</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.tableCell}>Account Profile & Credentials</td>
                <td style={styles.tableCell}>Until verified self-service deletion completes; access is blocked as soon as the deletion process begins</td>
                <td style={styles.tableCell}>Active profile, credentials, sessions, database content, and stored files are removed before the completion receipt is shown. Non-sensitive deletion receipts and temporary session-revocation records may remain for retry safety, audit, and security; lawful exceptions and provider backup cycles may also apply</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Uploaded PDF Documents</td>
                <td style={styles.tableCell}>Until a document or verified account-deletion process completes, subject to lawful exceptions and provider backup cycles</td>
                <td style={styles.tableCell}>Removed from active systems through the verified document/account deletion process; provider backups expire under their controlled lifecycle</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Generated Learning Cards & Quizzes</td>
                <td style={styles.tableCell}>Until the source document or verified account-deletion process completes, subject to lawful exceptions</td>
                <td style={styles.tableCell}>Removed through the verified document/account deletion process after dependent records are checked</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>User Interaction Events (clicks, swipes, views)</td>
                <td style={styles.tableCell}>12 months</td>
                <td style={styles.tableCell}>Deleted after the retention period through a scheduled or controlled retention process</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Study Session Metrics & Quiz Attempts</td>
                <td style={styles.tableCell}>24 months</td>
                <td style={styles.tableCell}>Deleted after the retention period through a scheduled or controlled retention process</td>
              </tr>
              <tr>
                <td style={styles.tableCell}>Technical Error & Crash Logs</td>
                <td style={styles.tableCell}>The provider-configured operational period needed for diagnosis, service integrity, and security</td>
                <td style={styles.tableCell}>Deleted or rotated under the applicable logging-provider process; a specific record may be preserved longer where necessary for an active security incident, legal claim, or binding obligation</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Your Rights */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Your Rights & Choices</h2>
          <p style={styles.paragraph}>You have the right to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Access:</strong> Request a copy of personal data we process about you; some account data is also visible in the app
            </li>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Correction:</strong> Correct inaccurate personal data in the app where available or by contacting us
            </li>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Deletion:</strong> Start self-service deletion in Settings or contact support. Access is blocked when the verified process begins, and completion is shown only after active Auth, database, and Storage checks pass; lawful exceptions and provider backup cycles may still apply
            </li>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Data Portability:</strong> Request qualifying data in a structured, commonly used, machine-readable format
            </li>
            <li style={styles.listItem}><strong style={styles.strong}>Restriction:</strong> Ask us to restrict qualifying processing</li>
            <li style={styles.listItem}><strong style={styles.strong}>Objection:</strong> Object to processing based on legitimate interests</li>
            <li style={styles.listItem}><strong style={styles.strong}>Withdraw Consent:</strong> Withdraw consent at any time where consent is the legal basis, without affecting earlier lawful processing</li>
            <li style={styles.listItem}><strong style={styles.strong}>Complaint:</strong> Lodge a complaint with a competent supervisory authority</li>
          </ul>
          <p style={styles.paragraph}>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
            We may need to verify your identity. We aim to respond within one month, subject to any lawful extension
            for a complex or numerous request. Rights may be limited where an exception under applicable law applies.
          </p>
          <p style={styles.paragraph}>
            <strong style={styles.strong}>Automated decision-making:</strong> ScrollEd uses AI to generate educational
            study materials at your request. It does not use that AI output to make solely automated decisions about
            you that produce legal effects or similarly significant effects within GDPR Article 22.
          </p>
        </section>

        {/* Age Policy & Children's Privacy */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Age Policy & Minors</h2>
          <p style={styles.paragraph}>
            ScrollEd is intended for adult users (aged 18 and older, or the age of legal majority in their jurisdiction).
            Minors aged 13 to 17 may use the Service only with verifiable parental or guardian consent. We do not
            knowingly collect or process personal data from children under 13 under any circumstances.
          </p>
          <p style={styles.paragraph}>
            If you are a parent or guardian and discover that your child under 18 has created an account without
            authorization, please contact us at <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
            Upon verification, we will promptly terminate access and remove active account data through the verified deletion process, subject to lawful retention exceptions and provider backup cycles.
          </p>
        </section>

        {/* International Transfers */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. International Data Transfers (GDPR Chapter V)</h2>
          <p style={styles.paragraph}>
            ScrollEd uses an EU-region Supabase project for its primary database and storage, but some providers and
            subprocessors may process data outside the EEA, including in the United States. Application hosting,
            authentication support, AI processing, email delivery, and OAuth may therefore involve international transfers.
          </p>
          <p style={styles.paragraph}>
            Where GDPR Chapter V applies to a transfer outside the EEA, the transfer must rely on an applicable
            adequacy decision, appropriate safeguards, or another lawful transfer mechanism. Depending on the provider
            and transfer, those measures may include:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Standard Contractual Clauses (SCCs):</strong> Where required, provider
              data-processing terms use the European Commission's approved SCCs (Commission Implementing Decision
              (EU) 2021/914) or another lawful Chapter V mechanism.
            </li>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Adequacy decisions:</strong> Where a provider is validly covered by an
              applicable adequacy decision, that decision may be used for the relevant transfer.
            </li>
            <li style={styles.listItem}>
              <strong style={styles.strong}>Technical safeguards:</strong> Encrypted transport, access controls, and
              provider security measures supplement the applicable legal transfer mechanism.
            </li>
          </ul>
        </section>

        {/* Changes to Policy */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Changes to This Privacy Policy</h2>
          <p style={styles.paragraph}>
            We may update this Privacy Policy to reflect changes in the Service, law, or our processing. The current
            version will be published in the app with a revised date. For a material change, we will take reasonable
            steps to provide additional notice through the Service or by email where required or appropriate.
          </p>
          <p style={styles.paragraph}>
            A change to this Policy does not create a new legal basis for processing that requires consent. Where new
            consent is required, it will be requested separately before that processing begins.
          </p>
        </section>

        {/* Contact, DPO status & complaint rights */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Contact, DPO Status & Complaints</h2>
          <p style={styles.paragraph}>
            If you have any questions or concerns about this Privacy Policy or wish to exercise your GDPR rights,
            please contact the Data Controller:
          </p>
          <div style={styles.contactBox}>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong style={styles.strong}>Data Controller:</strong> Independent Individual Operator
            </p>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong style={styles.strong}>Email / Inquiries:</strong>{' '}
              <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              <strong style={styles.strong}>Platform:</strong>{' '}
              <a href="https://scrolled.eu" target="_blank" rel="noopener noreferrer" style={styles.link}>
                scrolled.eu
              </a>
            </p>
          </div>

          <h3 style={{ ...styles.sectionTitle, fontSize: '16px', marginTop: '20px', borderBottom: 'none', paddingBottom: 0 }}>
            No Designated DPO
          </h3>
          <p style={styles.paragraph}>
            No separate DPO has been designated. The Controller handles privacy enquiries directly through{' '}
            <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>. A supervisory authority
            is an independent public regulator; it is not a person appointed by ScrollEd.
          </p>
          
          <h3 style={{ ...styles.sectionTitle, fontSize: '16px', marginTop: '20px', borderBottom: 'none', paddingBottom: 0 }}>
            Right to Lodge a Complaint (GDPR Art. 77)
          </h3>
          <p style={styles.paragraph}>
            If you are in the European Union, you may lodge a complaint with a competent data-protection supervisory
            authority, in particular in the Member State of your habitual residence, place of work, or place of the
            alleged infringement. You do not have to contact ScrollEd first, although we welcome the opportunity to
            address your concern at <a href="mailto:support@scrolled.eu" style={styles.link}>support@scrolled.eu</a>.
          </p>
          <p style={styles.paragraph}>
            The current list of EU supervisory authorities is available in the{' '}
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer" style={styles.link}>
              European Data Protection Board member directory
            </a>.
          </p>
        </section>
      </main>

      <footer style={styles.footer}>
        <p>&copy; {new Date().getFullYear()} ScrollEd. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPage;
